import React, { act, useEffect, useRef, useState } from 'react';
import 'bootstrap-select';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import StoreService from 'service/StoreService';
import { StoreUpdateReq } from 'domain/payload/request/store/StoreUpdateReq';
import { StoreCreateReq } from 'domain/payload/request/store/StoreCreateReq';
import LoadingSpinner from 'component/LoadingSpinner';
import BooleanSlider from 'component/BooleanSlider';
import { QRCodeCanvas } from 'qrcode.react';
import html2canvas from 'html2canvas';

const StoreForm: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);

  const canvasRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const storeService = StoreService();

  // INPUT STATE
  const [storeCode, setStoreCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [phone, setPhone] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [latLong, setLatLong] = useState('');
  const [regionCode, setRegionCode] = useState('');

  const [hasQr, setHasQr] = useState(false);

  const [referenceId, setReferenceId] = useState('');
  const [referenceType, setReferenceType] = useState('');

  // LOADING
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoadingSave(true)
    try {
      if (id == "0") {
        await storeCreate()
        alert(`Data ${name} successfully created!`)
        navigate("/store")
      } else {
        await storeUpdate()
        alert(`Data ${name} successfully updated!`)
        navigate("/store")
      }
      setIsLoadingSave(false)
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat menyimpan data';
      alert(`Error: ${errorMessage}`);
      setIsLoadingSave(false)
    }
  };

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingDetail(true)
    try {
      const res = await storeService.storeDetail(token, id ?? "0");
      console.log(res)
      setName(res.data?.name!)
      setDescription(res.data?.description!)
      setStoreCode(res.data?.code!)
      setPhone(res.data?.phone!)
      setContactPerson(res.data?.contactPerson!)
      setAddress(res.data?.address!)
      setCity(res.data?.city!)
      setLatLong(res.data?.latitude + "," + res.data?.longitude)
      setRegionCode(res.data?.regionCode!)
      setHasQr(res.data?.hasQr!)
      setReferenceId(res.data?.referenceId!)
      setReferenceType(res.data?.referenceType!)

      setIsLoadingDetail(false)
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail store';
      window.alert(`Error: ${errorMessage}`);
      setIsLoadingDetail(false)
    }
  };

  const storeCreate = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      var request: StoreCreateReq = {
        name: name,
        description: description,
        code: storeCode,
        address: address,
        phone: phone,
        contactPerson: contactPerson,
        city: city,
        latitude: Number(latLong.split(",")[0].trim()),
        longitude: Number(latLong.split(",")[1].trim()),
        regionCode: regionCode,
        referenceId: referenceId,
        referenceType: referenceType,
        hasQr: hasQr
      }

      console.log(request)

      await storeService.storeCreate(token, request);
    } catch (err) {
      console.error(err);
      throw err
    }
  }

  const storeUpdate = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      var request: StoreUpdateReq = {
        id: id!,
        name: name,
        description: description,
        code: storeCode,
        address: address,
        phone: phone,
        contactPerson: contactPerson,
        city: city,
        latitude: Number(latLong.split(",")[0].trim()),
        longitude: Number(latLong.split(",")[1].trim()),
        regionCode: regionCode,
        referenceId: referenceId,
        referenceType: referenceType,
        hasQr: hasQr
      }

      request.latitude = Number(latLong.split(",")[0].trim())
      request.longitude = Number(latLong.split(",")[1].trim())

      console.log(request)

      await storeService.storeUpdate(token, request);
    } catch (err) {
      console.error(err);
      throw err
    }
  }

  const storeGenerateCode = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      let res = await storeService.storeGenerateCode(token);
      setStoreCode(res.data!)
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat generate kode';
      window.alert(`Error: ${errorMessage}`);
    }
  }

  const viewMaps = async () => {
    if (!latLong || !latLong.includes(',')) {
      alert("Invalid latitude,longitude format");
      return;
    }

    const [latStr, longStr] = latLong.split(",");
    const tempLat = parseFloat(latStr.trim());
    const tempLong = parseFloat(longStr.trim());

    if (
      isNaN(tempLat) || isNaN(tempLong) ||
      tempLat < -90 || tempLat > 90 ||
      tempLong < -180 || tempLong > 180
    ) {
      alert("Invalid latitude or longitude value");
      return;
    }

    const mapsUrl = `https://www.google.com/maps?q=${tempLat},${tempLong}`;
    window.open(mapsUrl, "_blank");
  };


  const downloadQRCode = async () => {
    if (!canvasRef.current) return;

    const canvas = await html2canvas(canvasRef.current, {
      backgroundColor: "#ffffff", // agar tidak transparan
    });

    const url = canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.href = url;
    link.download = `${storeCode}_qr.png`;
    link.click();
  };



  useEffect(() => {
    if (id != "0") {
      fetchDetail()
    }
  }, [])

  return (

    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">{id == "0" ? "Add Store" : "Edit Store"}</h4>
        <div className="p-4" style={{ background: 'white' }}>
          {
            isLoadingDetail ?
              <LoadingSpinner />
              :
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-sm-6">
                    <div>
                      <h5 className="mb-4">Basic Information</h5>
                      {/* <div className="mb-3">
                        <label htmlFor="code" className="form-label fw-semibold">Store Code</label>
                        <div className="d-flex">
                          <input
                            type="text"
                            id="code"
                            className="form-control form-control-compact"
                            value={storeCode}
                            placeholder="Store Code"
                            onChange={(e) => setStoreCode(e.target.value)}
                            required
                          />
                          <button type="button" className="btn btn-primary d-flex justify-content-center align-items-center ms-2" onClick={storeGenerateCode} disabled={isLoadingSave}>
                            Generate
                          </button>
                        </div>
                      </div> */}
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label fw-semibold">Name</label>
                        <input
                          type="text"
                          id="name"
                          className="form-control form-control-compact"
                          value={name}
                          placeholder="Name"
                          onChange={(e) => setName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label fw-semibold">Description</label>
                        <input
                          type="text"
                          id="description"
                          className="form-control form-control-compact"
                          value={description}
                          placeholder="Description"
                          onChange={(e) => setDescription(e.target.value)}
                          required
                        />
                      </div>
                      {/* <div className="mb-3">
                        <label htmlFor="phone" className="form-label fw-semibold">Phone</label>
                        <input
                          type="text"
                          id="phone"
                          className="form-control form-control-compact"
                          value={phone}
                          placeholder="Phone"
                          onChange={(e) => setPhone(e.target.value)}
                          required
                        />
                      </div> */}
                      {/* <div className="mb-3">
                        <label htmlFor="contactPerson" className="form-label fw-semibold">Contact Person</label>
                        <input
                          type="text"
                          id="contactPerson"
                          className="form-control form-control-compact"
                          value={contactPerson}
                          placeholder="Contact Person"
                          onChange={(e) => setContactPerson(e.target.value)}
                          required
                        />
                      </div> */}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div>
                      <h5 className="mb-4">Location</h5>
                      <div className="mb-3">
                        <label htmlFor="address" className="form-label fw-semibold">Address</label>
                        <input
                          type="text"
                          id="address"
                          className="form-control form-control-compact"
                          value={address}
                          placeholder="Address"
                          onChange={(e) => setAddress(e.target.value)}
                          required
                        />
                      </div>
                      {/* <div className="mb-3">
                        <label htmlFor="city" className="form-label fw-semibold">City</label>
                        <input
                          type="text"
                          id="city"
                          className="form-control form-control-compact"
                          value={city}
                          placeholder="City"
                          onChange={(e) => setCity(e.target.value)}
                          required
                        />
                      </div> */}
                      <div className="mb-3">
                        <label htmlFor="latLong" className="form-label fw-semibold">Latitude Longitude</label>
                        <div className="d-flex">
                          <input
                            type="text"
                            id="latLong"
                            className="form-control form-control-compact"
                            value={latLong}
                            placeholder="Latitude,Longitude"
                            onChange={(e) => setLatLong(e.target.value)}
                            required
                          />
                          <button type="button" className="btn btn-outline-primary d-flex justify-content-center align-items-center ms-2" onClick={viewMaps} disabled={isLoadingSave}>
                            View Map
                          </button>
                        </div>
                      </div>
                      {/* <div className="mb-3">
                        <label htmlFor="latLong" className="form-label fw-semibold">Region Code</label>
                        <input
                          type="text"
                          id="regionCode"
                          className="form-control form-control-compact"
                          value={regionCode}
                          placeholder="Region Code"
                          onChange={(e) => setRegionCode(e.target.value)}
                          required
                        />
                      </div> */}
                    </div>
                  </div>

                  <div className="mb-4" />

                  <div className="col-sm-6">
                    {/* <div>
                      <h5 className="mb-4">Store QR</h5>
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label fw-semibold">Generate QR Code for this store?</label><br />
                        <BooleanSlider value={hasQr} onChange={(val) => setHasQr(val)} label={''} />
                        {
                          hasQr &&
                          (
                            storeCode != null && storeCode != "" ?
                              <>
                                <div className="text-center">
                                  <div ref={canvasRef} className="text-center d-inline-block p-5">
                                    <p className="fw-bold">{storeCode}</p>
                                    <div>
                                      <QRCodeCanvas value={storeCode} size={256} />
                                    </div>
                                  </div>
                                  <br></br>
                                  <button type="button" className="btn btn-outline-primary" onClick={downloadQRCode}>
                                    Download
                                  </button>
                                </div>
                              </>
                              :
                              <>
                                Please input store code to preview QR Code
                              </>
                          )
                        }
                      </div>
                    </div> */}
                  </div>

                  <div className="col-sm-6">
                    <div>
                      <h5 className="mb-4">Additional Information</h5>
                      <div className="mb-3">
                        <label htmlFor="referenceId" className="form-label fw-semibold">Reference ID</label>
                        <input
                          type="text"
                          id="referenceId"
                          className="form-control form-control-compact"
                          value={referenceId}
                          placeholder="Reference ID"
                          onChange={(e) => setReferenceId(e.target.value)}
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="referenceType" className="form-label fw-semibold">Reference Type</label>
                        <input
                          type="text"
                          id="referenceType"
                          className="form-control form-control-compact"
                          value={referenceType}
                          placeholder="referenceType"
                          onChange={(e) => setReferenceType(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                </div>

                <div className="w-100 d-flex justify-content-end">
                  <Link to="/store">
                    <button className="btn btn-outline-primary">Cancel</button>
                  </Link>
                  <button type="submit" className="btn btn-primary d-flex justify-content-center align-items-center ms-2" disabled={isLoadingSave}>
                    {
                      isLoadingSave &&
                      <>
                        <LoadingSpinner reverse={true} />
                        <p className="ms-2"></p>
                      </>
                    }
                    Save
                  </button>
                </div>
              </form>
          }
        </div>
      </div>
    </div>
  );

};

export default StoreForm;